import React, { Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Skeleton, SparkIcon, sparkToast } from '@spark';
import SideTabsLayout from '@/shared/components/SideTabsLayout.jsx';
import LazyImportBoundary from '@/shared/components/LazyImportBoundary.jsx';
import ProfileInformation from '@/features/settings/components/ProfileInformation.jsx';
import UserManagement from '@/features/users/components/UserManagement.jsx';
import ModelProviders, { countConfiguredProviders } from '@/features/settings/components/ModelProviders.jsx';
import HardwareDevice from '@/features/settings/components/HardwareDevice.jsx';
import ExternalDeployTargetsForm from '@/features/settings/components/providers/ExternalDeployTargetsForm.jsx';
import HuggingFaceToken from '@/features/settings/components/HuggingFaceToken.jsx';
import TavilyApiKey from '@/features/settings/components/TavilyApiKey.jsx';
import ApiKeysSection from '@/features/settings/components/ApiKeysSection.jsx';
import McpSettings from '@/features/settings/components/McpSettings.jsx';
import {
    BILLING_TAB,
    CREDIT_USAGE_TAB,
    MCP_TAB,
    PLATFORM_SETTINGS_TAB_IDS,
    PROVIDERS_TAB_ID,
    WAITING_LIST_TAB,
    WORKSPACE_TAB,
    getVisibleSettingsTabs,
} from '@/features/settings/components/settingsTabs.js';
import WaitingListManagement from '@/features/waiting-list/components/WaitingListManagement.jsx';
import usePendingCreatorRequestCount from '@/features/waiting-list/hooks/usePendingCreatorRequestCount.js';
import WorkspaceSettingsTab from '@/features/workspaces/components/settings/WorkspaceSettingsTab.jsx';
import { data_from_server_post } from '@/shared/http/client';
import { useAuth } from '@/features/auth/AuthContext';
import {
    canManagePlatformSettings as canManagePlatformSettingsFor,
    canManageProviders as canManageProvidersFor,
} from '@/features/auth/providerAccess';
import { GlobalContext } from '@/app/providers/globalState';
import './settings.css';

const CLIENT_ID = "40b3a00e282ee2db1338c3bba881b99125fc3ba1d7f5c29232ce1ad63fab1e75";

// Lazy so the Stripe.js bundle only ships to SaaS admins who open the tab;
// self-hosted installs never download it. First React.lazy in the repo.
const PlansAndBilling = React.lazy(() => import('@/features/billing/components/PlansAndBilling.jsx'));
const CreditUsage = React.lazy(() => import('@/features/billing/components/CreditUsage.jsx'));

const BillingFallback = () => (
    <div className="settings-billing-fallback" aria-busy="true" aria-label="Loading Plans & Billing">
        <Skeleton shapeType="h2" width={220} />
        <Skeleton shapeType="rectangle" width="100%" height={1} />
        <Skeleton shapeType="h1" width={320} />
        <Skeleton shapeType="paragraph" width={360} />
        <Skeleton shapeType="rectangle" width={447} height={86} />
    </div>
);

// Design order (Figma 9244:4940 / 9396:5032 / 9851:18381): Workspace
// (prepended by ``getVisibleSettingsTabs``), Profile, User Management,
// Plans & Billing, Model Providers, …, MCP, API Keys. Everyone lands on
// Workspace; Admins also get User Management. On SaaS the platform-owned
// tabs (Model Providers, Hardware Device, External Deploy - Targets, Hugging
// Face Token, Tavily) are gods' only — the backend 403s their writes and
// redacts their reads regardless. MCP exists only while the backend reports
// ``mcp_enabled``.
const TAB_ITEMS = [
    { id: 'profile', label: 'Profile' },
    { id: 'users', label: 'User Management' },
    { id: 'providers', label: 'Model Providers' },
    { id: 'hardware', label: 'Hardware Device' },
    { id: 'deploy-targets', label: 'External Deploy - Targets' },
    { id: 'hf-token', label: 'Hugging Face Token' },
    { id: 'tavily-key', label: 'Tavily' },
    MCP_TAB,
    { id: 'api-keys', label: 'API Keys' },
];

// Every member lands on Workspace (read-only for Editors / Viewers).
const DEFAULT_TAB_ID = WORKSPACE_TAB.id;

const isKnownTab = (tabId, items) => items.some((item) => item.id === tabId);

export function Settings() {
    const auth = useAuth();
    const { user, role, platformRole, authMethod, isAuthEnabled, isSaas, billingEnabled, mcpEnabled } = auth;
    // SaaS keys are SpecificAI's: only gods get the Model Providers tab, and
    // likewise the hardware / deploy-target / personal-token tabs.
    const canManageProviders = canManageProvidersFor(auth);
    const canManagePlatformSettings = canManagePlatformSettingsFor(auth);
    const { refreshCommercialModels } = useContext(GlobalContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const isAdmin = !isAuthEnabled || role === 'Admin';
    const showBillingTab = isSaas === true && billingEnabled === true && isAdmin;
    // Mirrors the backend `require_god` bypass: auth-disabled installs are god.
    const isGod = !isAuthEnabled || platformRole === 'god';
    // "Waiting List (N)" — fetched only when the god-only tab is visible
    // (same gate as `getVisibleSettingsTabs`), so nobody else calls the route.
    const {
        count: pendingCreatorRequestCount,
        refresh: refreshPendingCreatorRequestCount,
    } = usePendingCreatorRequestCount({ enabled: isGod && isSaas === true });
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(() => {
        const tabFromUrl = new URLSearchParams(window.location.search).get('tab');
        if (tabFromUrl === PROVIDERS_TAB_ID && !canManageProviders) {
            return DEFAULT_TAB_ID;
        }
        if (PLATFORM_SETTINGS_TAB_IDS.includes(tabFromUrl) && !canManagePlatformSettings) {
            return DEFAULT_TAB_ID;
        }
        if (tabFromUrl === 'users' && !isAdmin) {
            return DEFAULT_TAB_ID;
        }
        if (tabFromUrl === MCP_TAB.id && !mcpEnabled) {
            return DEFAULT_TAB_ID;
        }
        if (
            TAB_ITEMS.some((item) => item.id === tabFromUrl)
            || ((tabFromUrl === BILLING_TAB.id || tabFromUrl === CREDIT_USAGE_TAB.id) && showBillingTab)
            || (tabFromUrl === WAITING_LIST_TAB.id && isGod)
            || tabFromUrl === WORKSPACE_TAB.id
        ) {
            return tabFromUrl;
        }
        return DEFAULT_TAB_ID;
    });
    const [settings, setSettings] = useState({
        hardware_device: 'gpu',
        openai_keys: [],
        anthropic_keys: [],
        google_keys: [],
        deepseek_keys: [],
        jev_keys: [],
        aws_access_key_id: '',
        aws_secret_access_key: '',
        aws_region: '',
        aws_s3_bucket: '',
        aws_role_arn: '',
        // Cross-account ECR push role (model "Push to ECR" flow).
        aws_ecr_assume_role_arn: '',
        aws_ecr_external_id: '',
        // DevOps-configured ECR push destinations ({uri, account_type, alias}).
        aws_ecr_targets: [],
        dockerhub_username: '',
        dockerhub_pat_configured: false,
        dockerhub_targets: [],
        // GCR / Artifact Registry push settings.
        gcp_gcr_targets: [],
        gcp_gcr_cross_project_sa_email: null,
        // Azure ACR push settings.
        azure_acr_targets: [],
        // Vertex settings are persisted on OrganizationSettings via
        // ``/save_vertex_credentials``. The ``vertex`` object mirrors
        // the persisted shape and feeds VertexCredentialsForm. It holds
        // no credential: Vertex authenticates with Workload Identity.
        vertex: {},
        // Which cloud the platform itself runs on, read-only from the
        // backend. The frontend image is cloud-agnostic, so this cannot
        // be derived here; the Vertex panel needs it to know whether
        // Workload Identity is usable at all.
        platform_cloud_provider: '',
        // Azure credentials are persisted on OrganizationSettings via
        // ``/save_azure_credentials``; the form mirrors them through
        // the flat ``azure_api_*`` fields the backend returns.
        azure: {},
        // Personal tokens arrive as masked metadata only (never the secret):
        // the backend reports whether one is stored plus its last 4 chars.
        hugging_face_token_configured: false,
        hugging_face_token_last4: null,
        tavily_api_key_configured: false,
        tavily_api_key_last4: null,
        // Org-wide recommended vendor provider ({provider, marked_by, marked_at}).
        recommended_provider: null,
    });
    const isAdminOrEditor = authMethod === 'NONE' ? true : (role === 'Admin' || role === 'Editor');
    const defaultTabId = DEFAULT_TAB_ID;

    const configuredProviderCount = useMemo(
        () => countConfiguredProviders(settings),
        [settings]
    );

    const tabItems = useMemo(
        () => getVisibleSettingsTabs({
            tabItems: TAB_ITEMS
                .filter((item) => item.id !== 'api-keys' || isAdminOrEditor)
                .filter((item) => item.id !== 'users' || isAdmin)
                .filter((item) => item.id !== MCP_TAB.id || mcpEnabled === true)
                .map((item) => {
                    // "Model Providers (N)" — configured providers, once the
                    // org settings have arrived. Until then the bare label
                    // avoids flashing "(0)" over the defaults.
                    if (item.id === 'providers' && !isLoading) {
                        return { ...item, label: `${item.label} (${configuredProviderCount})` };
                    }
                    return item;
                }),
            isAdmin,
            isGod,
            isSaas,
            billingEnabled,
            canManageProviders,
            canManagePlatformSettings,
            waitingListPendingCount: pendingCreatorRequestCount,
        }),
        [
            isAdmin,
            isAdminOrEditor,
            isGod,
            isLoading,
            configuredProviderCount,
            isSaas,
            billingEnabled,
            mcpEnabled,
            canManageProviders,
            canManagePlatformSettings,
            pendingCreatorRequestCount,
        ]
    );

    // Deep-link support: ``/settings?tab=providers`` opens Model Providers
    // (e.g. from Model Selection "Can't find it? Add a new key").
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl && isKnownTab(tabFromUrl, tabItems)) {
            setActiveTab(tabFromUrl);
            return;
        }
        setActiveTab(defaultTabId);
    }, [searchParams, tabItems, defaultTabId]);

    const handleTabChange = (nextTab) => {
        setActiveTab(nextTab);
        const nextParams = new URLSearchParams(searchParams);
        if (nextTab && nextTab !== defaultTabId) {
            nextParams.set('tab', nextTab);
        } else {
            nextParams.delete('tab');
        }
        setSearchParams(nextParams, { replace: true });
    };

    const handleBack = useCallback(() => {
        const from = location.state?.from;
        if (
            typeof from === 'string'
            && from.startsWith('/')
            && !from.startsWith('/settings')
        ) {
            navigate(from);
            return;
        }
        if (window.history.length > 1) {
            navigate(-1);
            return;
        }
        navigate('/');
    }, [location.state, navigate]);

    const fetchSettings = () => {
        setIsLoading(true);
        data_from_server_post(
            "get_organization_settings",
            CLIENT_ID,
            (result) => {
                const next = {
                    openai_keys: result.openai_keys || [],
                    anthropic_keys: result.anthropic_keys || [],
                    google_keys: result.google_keys || [],
                    deepseek_keys: result.deepseek_keys || [],
                    jev_keys: result.jev_keys || [],
                    aws_access_key_id: result.aws_access_key_id || '',
                    aws_secret_access_key: result.aws_secret_access_key || '',
                    aws_region: result.aws_region || '',
                    aws_s3_bucket: result.aws_s3_bucket || '',
                    aws_role_arn: result.aws_role_arn || '',
                    aws_ecr_assume_role_arn: result.aws_ecr_assume_role_arn || '',
                    aws_ecr_external_id: result.aws_ecr_external_id || '',
                    aws_ecr_targets: result.aws_ecr_targets || [],
                    dockerhub_username: result.dockerhub_username || '',
                    dockerhub_pat_configured: Boolean(result.dockerhub_pat_configured),
                    dockerhub_targets: result.dockerhub_targets || [],
                    gcp_gcr_targets: result.gcp_gcr_targets || [],
                    gcp_gcr_cross_project_sa_email: result.gcp_gcr_cross_project_sa_email || null,
                    azure_acr_targets: result.azure_acr_targets || [],
                    azure: {
                        azure_mode: result.azure_mode || 'openai',
                        azure_api_key: result.azure_api_key || '',
                        azure_api_base: result.azure_api_base || '',
                        azure_tenant_id: result.azure_tenant_id || '',
                        azure_client_id: result.azure_client_id || '',
                        azure_client_secret: result.azure_client_secret || '',
                        azure_foundry_project_endpoint: result.azure_foundry_project_endpoint || '',
                        azure_batch_deployments: result.azure_batch_deployments || '',
                    },
                    vertex: {
                        vertex_auth_mode: result.vertex_auth_mode || '',
                        vertex_effective_auth_mode: result.vertex_effective_auth_mode || '',
                        vertex_project: result.vertex_project || '',
                        vertex_location: result.vertex_location || '',
                        vertex_gcs_bucket: result.vertex_gcs_bucket || '',
                    },
                    platform_cloud_provider: result.platform_cloud_provider || '',
                    hugging_face_token_configured: Boolean(result.hugging_face_token_configured),
                    hugging_face_token_last4: result.hugging_face_token_last4 || null,
                    tavily_api_key_configured: Boolean(result.tavily_api_key_configured),
                    tavily_api_key_last4: result.tavily_api_key_last4 || null,
                    recommended_provider: result.recommended_provider || null,
                };
                if (next.openai_keys.length === 0 && result.openai_key) {
                    next.openai_keys = [result.openai_key];
                }
                setSettings(prev => ({ ...prev, ...next }));
                setIsLoading(false);
            },
            () => setIsLoading(false)
        );
        data_from_server_post(
            "get_user_settings",
            user.email,
            (result) => {
                setSettings(prev => ({ ...prev, hardware_device: result.hardware_device || 'gpu' }));
            },
            (error) => {
                sparkToast.error("Failed to fetch user settings: " + error);
            }
        );
    };

    useEffect(() => {
        fetchSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateHardwareDevice = (device) => {
        data_from_server_post(
            "save_hardware_device",
            {
                "client_id": CLIENT_ID,
                "hardware_device": device,
            },
            () => {
                setSettings(prev => ({ ...prev, hardware_device: device }));
                sparkToast.success("Hardware device preference saved successfully");
            },
            (error) => {
                sparkToast.error("Failed to save hardware device preference: " + error);
            }
        );
    };

    const updateVendorKeys = (provider, keys, action) => {
        data_from_server_post(
            "save_llm_keys",
            {
                "client_id": CLIENT_ID,
                "provider": provider,
                "api_keys": keys,
            },
            () => {
                setSettings(prev => ({ ...prev, [`${provider}_keys`]: keys }));
                sparkToast.success(`API keys ${action} successfully`);
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error("Failed to save API keys: " + error);
            }
        );
    };

    const updateRecommendedProvider = (provider) => {
        data_from_server_post(
            "save_recommended_provider",
            {
                "client_id": CLIENT_ID,
                "provider": provider,
            },
            (result) => {
                setSettings(prev => ({
                    ...prev,
                    recommended_provider: result?.recommended_provider || null,
                }));
                sparkToast.success(
                    provider
                        ? "Recommended provider saved successfully"
                        : "Recommended provider cleared successfully"
                );
            },
            (error) => {
                sparkToast.error("Failed to save recommended provider: " + error);
            }
        );
    };

    const updateBedrockCredentials = (fields, action) => {
        data_from_server_post(
            "save_aws_bedrock_credentials",
            {
                "client_id": CLIENT_ID,
                "aws_access_key_id": fields.aws_access_key_id || null,
                "aws_secret_access_key": fields.aws_secret_access_key || null,
                "aws_region": fields.aws_region || null,
                "aws_s3_bucket": fields.aws_s3_bucket || null,
                "aws_role_arn": fields.aws_role_arn || null,
            },
            () => {
                setSettings(prev => ({ ...prev, ...fields }));
                sparkToast.success(`Bedrock credentials ${action} successfully`);
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error("Failed to save Bedrock credentials: " + error);
            }
        );
    };

    const updateEcrSettings = (fields, action) => {
        data_from_server_post(
            "save_ecr_settings",
            {
                "client_id": CLIENT_ID,
                "aws_ecr_assume_role_arn": fields.aws_ecr_assume_role_arn || null,
                "aws_ecr_external_id": fields.aws_ecr_external_id || null,
                "aws_ecr_targets": fields.aws_ecr_targets || [],
            },
            () => {
                setSettings(prev => ({ ...prev, ...fields }));
                sparkToast.success(`ECR settings ${action} successfully`);
            },
            (error) => {
                sparkToast.error("Failed to save ECR settings: " + error);
            }
        );
    };

    const updateDockerHubSettings = (fields, action) => {
        data_from_server_post(
            "save_dockerhub_settings",
            {
                "client_id": CLIENT_ID,
                "dockerhub_username": fields.dockerhub_username || null,
                "dockerhub_pat": fields.dockerhub_pat || null,
                "dockerhub_pat_update": Boolean(fields.dockerhub_pat_update),
                "dockerhub_targets": fields.dockerhub_targets || [],
            },
            () => {
                setSettings(prev => ({
                    ...prev,
                    dockerhub_username: fields.dockerhub_username || '',
                    dockerhub_pat_configured: fields.dockerhub_pat_update
                        ? Boolean(fields.dockerhub_pat)
                        : prev.dockerhub_pat_configured,
                    dockerhub_targets: fields.dockerhub_targets || [],
                }));
                sparkToast.success(`Docker Hub settings ${action} successfully`);
            },
            (error) => {
                sparkToast.error("Failed to save Docker Hub settings: " + error);
            }
        );
    };

    const updateGcrSettings = (fields, action) => {
        data_from_server_post(
            "save_gcr_settings",
            {
                "client_id": CLIENT_ID,
                "gcp_gcr_targets": fields.gcp_gcr_targets || [],
                "gcp_gcr_cross_project_sa_email": fields.gcp_gcr_cross_project_sa_email || null,
            },
            () => {
                setSettings((prev) => ({
                    ...prev,
                    gcp_gcr_targets: fields.gcp_gcr_targets || [],
                    gcp_gcr_cross_project_sa_email: fields.gcp_gcr_cross_project_sa_email || null,
                }));
                sparkToast.success(`GCR settings ${action} successfully`);
            },
            (error) => {
                sparkToast.error("Failed to save GCR settings: " + error);
            }
        );
    };

    const updateAcrSettings = (fields, action) => {
        data_from_server_post(
            "save_acr_settings",
            {
                "client_id": CLIENT_ID,
                "azure_acr_targets": fields.azure_acr_targets || [],
            },
            () => {
                setSettings((prev) => ({
                    ...prev,
                    azure_acr_targets: fields.azure_acr_targets || [],
                }));
                sparkToast.success(`ACR settings ${action} successfully`);
            },
            (error) => {
                sparkToast.error("Failed to save ACR settings: " + error);
            }
        );
    };

    const clearBedrockCredentials = () => {
        data_from_server_post(
            "clear_aws_bedrock_credentials",
            {},
            () => {
                setSettings(prev => ({
                    ...prev,
                    aws_access_key_id: '',
                    aws_secret_access_key: '',
                    aws_region: '',
                    aws_s3_bucket: '',
                    aws_role_arn: '',
                }));
                sparkToast.success("Bedrock credentials cleared successfully");
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error("Failed to clear Bedrock credentials: " + error);
            }
        );
    };

    const updateVertexCredentials = (payload, action) => {
        data_from_server_post(
            "save_vertex_credentials",
            { "client_id": CLIENT_ID, ...payload },
            () => {
                setSettings(prev => ({ ...prev, vertex: payload }));
                sparkToast.success(`VertexAI settings ${action} successfully`);
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error("Failed to save VertexAI settings: " + error);
            }
        );
    };

    const clearVertexCredentials = () => {
        data_from_server_post(
            "clear_vertex_credentials",
            {},
            () => {
                // Re-read rather than clearing optimistically: on a GCP
                // platform the provider stays registered by default, and
                // only the server knows that.
                fetchSettings();
                sparkToast.success("VertexAI settings cleared successfully");
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error("Failed to clear VertexAI settings: " + error);
            }
        );
    };

    // Resolves rather than rejects on failure: the form renders the GCP
    // error text inline, which is the only place a missing IAM role
    // becomes visible.
    const testVertexConnection = () =>
        new Promise((resolve) => {
            data_from_server_post(
                "test_vertex_connection",
                {},
                (result) => resolve(result),
                (error) => resolve({ success: false, error: String(error) })
            );
        });

    const updateAzureCredentials = (fields, action) => {
        data_from_server_post(
            "save_azure_credentials",
            {
                "client_id": CLIENT_ID,
                "azure_mode": fields.azure_mode || null,
                "azure_api_key": fields.azure_api_key || null,
                "azure_api_base": fields.azure_api_base || null,
                "azure_tenant_id": fields.azure_tenant_id || null,
                "azure_client_id": fields.azure_client_id || null,
                "azure_client_secret": fields.azure_client_secret || null,
                "azure_foundry_project_endpoint": fields.azure_foundry_project_endpoint || null,
                "azure_batch_deployments": fields.azure_batch_deployments || null,
            },
            () => {
                setSettings(prev => ({ ...prev, azure: fields }));
                sparkToast.success(`AzureAI credentials ${action} successfully`);
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error("Failed to save AzureAI credentials: " + error);
            }
        );
    };

    const clearAzureCredentials = () => {
        data_from_server_post(
            "clear_azure_credentials",
            {},
            () => {
                setSettings(prev => ({
                    ...prev,
                    azure: {
                        azure_mode: 'openai',
                        azure_api_key: '',
                        azure_api_base: '',
                        azure_tenant_id: '',
                        azure_client_id: '',
                        azure_client_secret: '',
                        azure_foundry_project_endpoint: '',
                        azure_batch_deployments: '',
                    },
                }));
                sparkToast.success("AzureAI credentials cleared successfully");
                refreshCommercialModels();
            },
            (error) => {
                sparkToast.error("Failed to clear AzureAI credentials: " + error);
            }
        );
    };

    // Only masked metadata is kept in state after a save; the plaintext the
    // user just typed is sent once and dropped.
    const updateHuggingFaceToken = (token, action) => {
        data_from_server_post(
            token ? "save_hugging_face_token" : "delete_hugging_face_token",
            token ? { hugging_face_token: token } : {},
            () => {
                setSettings(prev => ({
                    ...prev,
                    hugging_face_token_configured: Boolean(token),
                    hugging_face_token_last4: token ? token.slice(-4) : null,
                }));
                sparkToast.success(`Hugging Face token ${action} successfully`);
            },
            (error) => {
                sparkToast.error("Failed to save Hugging Face token: " + error);
            }
        );
    };

    const updateTavilyApiKey = (apiKey, action) => {
        data_from_server_post(
            apiKey ? "save_tavily_api_key" : "delete_tavily_api_key",
            apiKey ? { tavily_api_key: apiKey } : {},
            () => {
                setSettings(prev => ({
                    ...prev,
                    tavily_api_key_configured: Boolean(apiKey),
                    tavily_api_key_last4: apiKey ? apiKey.slice(-4) : null,
                }));
                sparkToast.success(`Tavily API key ${action} successfully`);
            },
            (error) => {
                sparkToast.error("Failed to save Tavily API key: " + error);
            }
        );
    };

    const renderSection = () => {
        if (activeTab === WORKSPACE_TAB.id) {
            return <WorkspaceSettingsTab />;
        }
        if (activeTab === 'users') {
            return <UserManagement />;
        }
        if (activeTab === 'profile') {
            return <ProfileInformation />;
        }
        if (activeTab === PROVIDERS_TAB_ID && canManageProviders) {
            return (
                <ModelProviders
                    settings={settings}
                    onVendorKeysUpdate={updateVendorKeys}
                    onRecommendedProviderUpdate={updateRecommendedProvider}
                    onBedrockUpdate={updateBedrockCredentials}
                    onBedrockClear={clearBedrockCredentials}
                    onVertexUpdate={updateVertexCredentials}
                    onVertexClear={clearVertexCredentials}
                    onVertexTestConnection={testVertexConnection}
                    onAzureUpdate={updateAzureCredentials}
                    onAzureClear={clearAzureCredentials}
                    initialProvider={searchParams.get('provider')}
                    focusField={searchParams.get('focus')}
                    platformCloudProvider={settings.platform_cloud_provider}
                />
            );
        }
        // Platform-owned sections never mount for a SaaS non-god, even if
        // ``activeTab`` is forced: the backend would 403 the writes anyway,
        // and the redacted read has nothing for these forms to show.
        if (activeTab === 'hardware' && canManagePlatformSettings) {
            return (
                <HardwareDevice
                    selectedDevice={settings.hardware_device}
                    onDeviceChange={updateHardwareDevice}
                />
            );
        }
        if (activeTab === 'deploy-targets' && canManagePlatformSettings) {
            return (
                <ExternalDeployTargetsForm
                    settings={settings}
                    onEcrSettingsUpdate={updateEcrSettings}
                    onDockerHubSettingsUpdate={updateDockerHubSettings}
                    onGcrSettingsUpdate={updateGcrSettings}
                    onAcrSettingsUpdate={updateAcrSettings}
                />
            );
        }
        if (activeTab === 'hf-token' && canManagePlatformSettings) {
            return (
                <HuggingFaceToken
                    configured={settings.hugging_face_token_configured}
                    last4={settings.hugging_face_token_last4}
                    onTokenUpdate={updateHuggingFaceToken}
                />
            );
        }
        if (activeTab === 'tavily-key' && canManagePlatformSettings) {
            return (
                <TavilyApiKey
                    configured={settings.tavily_api_key_configured}
                    last4={settings.tavily_api_key_last4}
                    onApiKeyUpdate={updateTavilyApiKey}
                />
            );
        }
        if (activeTab === MCP_TAB.id && mcpEnabled === true) {
            return <McpSettings />;
        }
        if (activeTab === 'api-keys') {
            return <ApiKeysSection />;
        }
        if (activeTab === WAITING_LIST_TAB.id) {
            return <WaitingListManagement onRequestsChanged={refreshPendingCreatorRequestCount} />;
        }
        if (activeTab === BILLING_TAB.id) {
            return (
                <LazyImportBoundary>
                    <Suspense fallback={<BillingFallback />}>
                        <PlansAndBilling />
                    </Suspense>
                </LazyImportBoundary>
            );
        }
        if (activeTab === CREDIT_USAGE_TAB.id) {
            return (
                <LazyImportBoundary>
                    <Suspense fallback={<Skeleton shapeType="rectangle" width="100%" height={240} />}>
                        <CreditUsage />
                    </Suspense>
                </LazyImportBoundary>
            );
        }
        return null;
    };

    // Sections that own their own data loading (or, like MCP, need none) and
    // must not be replaced by the generic settings placeholder while
    // ``/settings`` is in flight.
    const sectionOwnsLoading = activeTab === 'users'
        || activeTab === WORKSPACE_TAB.id
        || activeTab === WAITING_LIST_TAB.id
        || activeTab === BILLING_TAB.id
        || activeTab === CREDIT_USAGE_TAB.id
        || activeTab === MCP_TAB.id;
    const showSettingsLoading = isLoading && !sectionOwnsLoading;

    return (
        <div className="settings-page settings">
            <button
                type="button"
                className="settings-page__back fs-unmask"
                onClick={handleBack}
            >
                <SparkIcon slug="arrow-left" size="small" />
                <span>Back</span>
            </button>
            <h1 className="settings-page__title fs-unmask">Settings</h1>
            <SideTabsLayout
                items={tabItems}
                activeId={activeTab}
                onChange={handleTabChange}
            >
                <div className={`settings-card${showSettingsLoading ? ' settings-card--loading' : ''}`}>
                    {showSettingsLoading ? 'Loading settings...' : renderSection()}
                </div>
            </SideTabsLayout>
        </div>
    );
}
